import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { SeriesOptionsType } from 'highcharts';
import produce from 'immer';
import classNames from 'classnames';
import { useQuery } from '@apollo/client';
import ResizePanel from 'react-resize-panel-ts';

import * as K from 'common/constants';
import { getLinkForRoute } from 'common/util/location';
import { adjustBucketAtMinMax, getChartType, getPlotGroups } from 'common/util/graph';
import { simplifyFilter } from 'common/util/filter/filter';
import { EventCacheData } from 'hooks/useLRQ/useEventCache';
import { useGlobal } from 'hooks/useGlobal';
import { useIsLabEnabled } from 'hooks/useIsLabEnabled';
import { Annotation, Plot } from 'common/graphqlTypes';
import { PlotModel, UrlParams } from 'common/types';

import { ANNOTATIONS_QUERY } from 'components/common/Annotations/queries';
import SearchGraphLegend from 'components/common/Legends/SearchGraphLegend/SearchGraphLegend';
import ScalyrGraph from 'components/common/Graphs/ScalyrGraph';
import { queryTimeRangeState, searchParamRecordState } from 'components/common/Managers/RouteStateManager.state';
import LineChartSkeleton from 'components/skeleton/LineChartSkeleton';

import { plotsStylesState } from 'styles/stylesProvider';
import './GraphResults.scss';
import GraphHeader from './GraphHeader';

type GraphResultsProps = {
  isDimmed:         boolean;
  isLoading:        boolean;
  isPreviousDataAvailable: boolean;
  results:          EventCacheData[];
  noTimelineLegend: boolean;
};

/**
 * This component acts as a placeholder while the data is being loaded
 */
function GraphResultsSkeleton(): JSX.Element {
  return (
    <div className="GraphResults__Skeleton skeleton-opacity-animation">
      <LineChartSkeleton />
    </div>
  );
}

/**
 * GraphResults component. Contains the Graph itself on the SearchGraphsPage as well as its associated Legend and TimelineLegend
 * and will handle the communication between them.
 *
 * @prop isLoading                  if true, results are not yet available
 * @prop isPreviousDataAvailable    signals that a skeleton UI should not be applied
 * @prop results                    LRQ result data payload from the eventCache
 * @prop noTimelineLegend           if true, do not show a legend above the graph
 */
function GraphResults({
  isDimmed,
  isLoading,
  isPreviousDataAvailable,
  results,
  noTimelineLegend,
}: GraphResultsProps): JSX.Element {
  const [ plotGroupData, setPlotGroupData ] = useState<Record<string, PlotModel[]>>({});
  const [ seriesData, setSeriesData ] = useGlobal(K.GLOBAL_KEY.GRAPH_SERIES_DATA);
  const [ timeStamps, setTimeStamps ] = useState<Array<number>>([]);
  const [ barWidth, setBarWidth ] = useState(0);
  const searchParams = useRecoilValue(searchParamRecordState);
  const graphRef = useRef<HTMLDivElement | null >(null);
  const plotsStyles = useRecoilValue(plotsStylesState);
  const { startTime, endTime } = useRecoilValue(queryTimeRangeState);
  const isRangedAnnotationsLabEnabled = useIsLabEnabled(K.LAB.BETA_UI_RANGED_ANNOTATIONS);
  const { data: annotationResults } = useQuery<{annotations: Annotation[]}>(ANNOTATIONS_QUERY, {
    skip: isRangedAnnotationsLabEnabled ? !startTime && !endTime : true,
    variables: {
      startMs: parseInt(startTime, 10),
      endMs: parseInt(endTime, 10),
    },
  });

  // TODO: This useEffect is growing by the minute, refactor to custom hook.
  useEffect(() => {
    const xAxis = results?.[0]?.plotData?.xAxis || [];
    const xValues = [...xAxis];

    let yValues: Array<Plot> = [];
    results?.forEach((result) => { yValues = yValues.concat(...result?.plotData?.plots || []); });
    const sliceInfo = results?.[0]?.plotData?.sliceInfo;

    if (sliceInfo && searchParams.graphStyle === K.CHART_TYPES.STACKED_BAR) {
      // Override the first and the last value to properly align the last bar
      const [ min, max ] = adjustBucketAtMinMax(xValues[0], xValues[xValues.length - 1], sliceInfo);
      xValues[0] = min;
      xValues[xValues.length - 1] = max;
      setBarWidth(sliceInfo.defaultSize);
    }

    const plotGroups = getPlotGroups(
      xValues,
      yValues,
      searchParams.filter,
      searchParams.facet,
      searchParams.breakdownFacet,
      searchParams.compareTimeOffset,
      searchParams.graphStyle,
      searchParams.showGraphs,
      searchParams.hideGraphs,
      plotsStyles,
    );

    const initialSeriesData: Array<SeriesOptionsType> = [];

    const chartType = getChartType(searchParams.graphStyle, searchParams.lineSmoothing);
    const offsetWidth = graphRef.current?.offsetWidth; // Width of the container of the graph
    // Increase the width of the bars, decrease spacing between each bar.
    const pointWidth = offsetWidth ? Math.floor((offsetWidth / (K.DEFAULT_COLUMN_SPACING * xValues.length))) : undefined;
    const isStacking = [K.SERIES_TYPES.COLUMN, K.CHART_TYPES.AREA, K.CHART_TYPES.AREA_SPLINE].includes(chartType);
    Object.values(plotGroups || {}).forEach((plotList: Array<PlotModel>) => {
      plotList.forEach((plot) => {
        initialSeriesData.push({
          data: plot.data,
          type: chartType,
          name: plot.name,
          visible: plot.visible,
          color: plot.color,
          pointWidth: chartType === K.SERIES_TYPES.COLUMN ? pointWidth : undefined,
          stacking: isStacking ? 'normal' : undefined,
          custom: {
            op: plot.op,
          },
        });
      });
    });
    setPlotGroupData(plotGroups);
    setTimeStamps(xValues);
    setSeriesData(initialSeriesData);
  }, [
    results,
    searchParams.filter,
    searchParams.facet,
    searchParams.breakdownFacet,
    searchParams.graphStyle,
    searchParams.lineSmoothing,
    searchParams.compareTimeOffset,
    searchParams.showGraphs,
    searchParams.hideGraphs,
    setSeriesData,
    plotsStyles,
  ]);

  // Fired when window resize occurs
  const recalcBarWidth = (): void => {
    const xAxis = results?.[0]?.plotData?.xAxis || [];
    const offsetWidth = graphRef.current?.offsetWidth; // Width of the container of the graph
    const pointWidth = offsetWidth ? Math.floor((offsetWidth / (K.DEFAULT_COLUMN_SPACING * xAxis.length))) : undefined;
    setSeriesData(produce(seriesData, (draft: Array<SeriesOptionsType>) => {
      draft.forEach((series) => {
        if (series.type === K.SERIES_TYPES.COLUMN) {
          series.pointWidth = pointWidth;
        }
      });
    }));
  };

  const newParams = { [K.URL_PARAM.FILTER]: simplifyFilter(searchParams[K.URL_PARAM.FILTER]) } as UrlParams;
  const logsPageLink = getLinkForRoute(K.ROUTE.EVENTS, searchParams, newParams);

  let graphHeader = null;
  if (!noTimelineLegend) {
    graphHeader = (
      <GraphHeader
        facet={searchParams.facet}
        breakdownFacet={searchParams.breakdownFacet}
        compareTimeOffset={searchParams.compareTimeOffset}
        logsPageLink={logsPageLink}
        isBarChart={searchParams.graphStyle === K.CHART_TYPES.STACKED_BAR}
        barWidth={barWidth}
        filter={searchParams.filter}
      />
    );
  }

  let graphView = (
    <ScalyrGraph
      seriesData={seriesData}
      annotationsData={annotationResults?.annotations}
      recalcBarWidth={recalcBarWidth}
    />
  );

  if (isLoading && !isPreviousDataAvailable) {
    graphView = <GraphResultsSkeleton />;
  }

  return (
    <>
      <div className={classNames('GraphResults__GraphArea', { 'dimming-overlay-effect': isDimmed })}>
        {graphHeader}
        <div className="GraphResults__Graph" ref={graphRef}>
          { graphView }
        </div>
      </div>
      <ResizePanel direction="w" handleClass='GraphResults__ResizePanelHandle'>
        <SearchGraphLegend
          plotGroups={plotGroupData}
          timeStamps={timeStamps}
        />
      </ResizePanel>
    </>
  );
}

export default GraphResults;
